import type {AdSample} from '../types/AdSample';
import type {AnalyticsConfig, AnalyticsDebugConfig} from '../types/AnalyticsConfig';
import type {Sample} from '../types/Sample';
import {logger} from '../utils/Logger';

import type {Backend} from './Backend';

/**
 * The `DebuggingBackend` is responsible for logging the debugging information into the console
 * before forwarding the requests to the specified underlying backend.
 *
 * This allows for inspection of specific sample / ad-sample fields before it is sent.
 * The fields can be specified through the {@link AnalyticsDebugConfig#fields} or {@link AnalyticsDebugConfig#adFields}.
 *
 * @implements {Backend}
 * */
export class DebuggingBackend implements Backend {
  private readonly underlying: Backend;

  private readonly sampleFields: string[] | undefined;
  private readonly adSampleFields: string[] | undefined;

  constructor(config: AnalyticsConfig, underlying: Backend) {
    this.underlying = underlying;

    this.sampleFields = (config.debug as AnalyticsDebugConfig)?.fields;
    this.adSampleFields = (config.debug as AnalyticsDebugConfig)?.adFields;
  }

  sendRequest(sample: Sample): void {
    logFieldsAsTable(this.sampleFields, sample);
    this.underlying.sendRequest(sample);
  }

  sendUnloadRequest(sample: Sample): void {
    logFieldsAsTable(this.sampleFields, sample);
    this.underlying.sendUnloadRequest(sample);
  }

  sendAdRequest(adSample: AdSample): void {
    logFieldsAsTable(this.adSampleFields, adSample);
    this.underlying.sendAdRequest(adSample);
  }

  sendUnloadAdRequest(adSample: AdSample): void {
    logFieldsAsTable(this.adSampleFields, adSample);
    this.underlying.sendUnloadAdRequest(adSample);
  }
}

function logFieldsAsTable(fields: string[] | undefined, s: AdSample | Sample) {
  if (fields?.length) {
    const loggedFields: Record<string, unknown> = {};
    // `fields` are arbitrary user-provided strings (from AnalyticsDebugConfig), not known keys of the sample,
    // so the index access cannot be statically typed.
    fields.forEach((f) => (loggedFields[f] = (s as Record<string, unknown>)[f]));
    logger.table([loggedFields]);
  }
}
